import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { Web3DViewer } from '@technology/web3d';
import {
  CADEventName,
  LineEditingAction,
} from '@technology/web3d-plugin-line-editing';
import {
  FeatureEditingController,
  PointCloudCrsAuthoring,
  SceneToGeographic,
  projectFeatureSceneAnchor,
  type LonLatProjector,
} from '@trimble/geospatial';
import { ILogger } from '@trimble/logging';
import { batch } from 'solid-js';

export interface CreateFeatureEditingBridgeParams {
  readonly viewer: Web3DViewer;
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  getSceneToGeographic(): SceneToGeographic | undefined;
  getPointCloudCrsAuthoring(): PointCloudCrsAuthoring | undefined;
  projectSceneAnchor?: LonLatProjector;
}

export interface FeatureEditingBridge {
  dispose(): void;
}

export function createFeatureEditingBridge(
  params: CreateFeatureEditingBridgeParams
): FeatureEditingBridge {
  const {
    viewer,
    context,
    logger,
    getSceneToGeographic,
    getPointCloudCrsAuthoring,
    projectSceneAnchor,
  } = params;
  const controller = new FeatureEditingController({
    getFeatureService: () => {
      const repo = context.featureRepository();
      if (repo === undefined) {
        throw new Error('Feature repository is not available');
      }
      return repo;
    },
    logger,
    getScope: () => context.scope(),
    getActiveLayer: () => context.selectedLayer(),
    getSceneToGeographic,
    getPointCloudCrsAuthoring,
    // Rotate after writes so CloudFront does not serve pre-edit tiles (shared).
    onFeaturesChanged: () => context.bumpTileCacheToken(),
    onFeaturePersisted: (feature) => {
      const featureId = feature.id;
      if (!featureId) {
        return;
      }
      batch(() => {
        context.upsertFeature(feature);
        if (projectSceneAnchor) {
          const anchor = projectFeatureSceneAnchor(feature, projectSceneAnchor);
          if (anchor) {
            context.setSceneAnchors({ [featureId]: anchor });
          }
        }
      });
    },
    onFeaturesRemoved: (featureIds) => {
      batch(() => {
        featureIds.forEach((id) => {
          context.removeFeature(id);
        });
        const selectedId = context.editingState.selectedFeature?.id;
        if (selectedId !== undefined && featureIds.includes(selectedId)) {
          context.setSelectedFeature(undefined);
        }
      });
    },
  });

  controller.start((handler) => {
    const actionListener = (event: Event): void => {
      const detail = (event as CustomEvent<LineEditingAction>).detail;
      if (detail) {
        handler(detail);
      }
    };
    viewer.addEventListener(CADEventName.ActionPerformed, actionListener);
    return () => {
      viewer.removeEventListener(CADEventName.ActionPerformed, actionListener);
    };
  });

  return {
    dispose: () => {
      controller.stop();
    },
  };
}
